/*
 * Hover behavior for the Sunny Cords cards mega menu (sc-header.md).
 *
 * The vendor hoverMenu() opens on mouseover and closes on mouseleave with
 * zero delay. With the panel hanging 8px below the pill that closes the
 * menu while the mouse travels down to it, and sweeping along the bar
 * flips every panel open. Two capturing document listeners intercept the
 * events before the vendor's own (capture always runs document -> target,
 * even for non-bubbling mouseleave) and replace them with:
 *  - a 120ms open threshold on a fresh hover (no accidental opens);
 *  - instant switching while another panel is already open;
 *  - a 200ms close delay, cancelled when the pointer re-enters;
 *  - synchronous close of sibling panels right before opening.
 * Only <details data-hover="true"> with a direct .sc-navmenu child are
 * intercepted; default dropdowns keep the vendor behavior. Queries run
 * against the live DOM so section reloads in the editor need no rebinding.
 */

const HOVER_OPEN_DELAY = 120;
const HOVER_CLOSE_DELAY = 200;

const timers = new WeakMap();

function isScDetails(el) {
  return (
    el instanceof Element &&
    el.tagName === 'DETAILS' &&
    el.dataset.hover === 'true' &&
    el.closest('[data-section-type="header"]') !== null &&
    el.querySelector(':scope > .sc-navmenu') !== null
  );
}

function allScDetails() {
  return Array.from(
    document.querySelectorAll('[data-section-type="header"] details[data-hover="true"]')
  ).filter(isScDetails);
}

function getTimers(details) {
  let entry = timers.get(details);
  if (!entry) {
    entry = {};
    timers.set(details, entry);
  }
  return entry;
}

function closeNow(details) {
  details.removeAttribute('open');
  details.setAttribute('aria-expanded', 'false');
}

function openNow(details) {
  allScDetails().forEach((other) => {
    if (other !== details && other.hasAttribute('open')) closeNow(other);
  });
  details.setAttribute('open', '');
  details.setAttribute('aria-expanded', 'true');
}

document.addEventListener(
  'mouseover',
  (event) => {
    if (!(event.target instanceof Element)) return;
    const details = event.target.closest('details[data-hover="true"]');
    if (!details || !isScDetails(details)) return;

    event.stopPropagation();
    const entry = getTimers(details);
    clearTimeout(entry.close);
    if (details.hasAttribute('open')) return;

    const switching = allScDetails().some((other) => other.hasAttribute('open'));
    clearTimeout(entry.open);
    entry.open = setTimeout(() => openNow(details), switching ? 0 : HOVER_OPEN_DELAY);
  },
  true
);

document.addEventListener(
  'mouseleave',
  (event) => {
    const details = event.target;
    if (!isScDetails(details)) return;

    event.stopPropagation();
    const entry = getTimers(details);
    clearTimeout(entry.open);
    clearTimeout(entry.close);
    if (!details.hasAttribute('open')) return;

    entry.close = setTimeout(() => closeNow(details), HOVER_CLOSE_DELAY);
  },
  true
);

/*
 * Two-step mobile menu sheet (sc-nav-drawer.liquid): main panel with
 * categories, one sub panel per category. theme.Drawers still owns
 * opening/closing #NavDrawer; this only handles panel switching, the
 * sheet position under the bar, and the sc-header.md behavior pitfalls:
 * synchronous panel reset before (re)opening (valkuil 12), height
 * animation measured on the inner sheet (valkuil 13), and closing on a
 * breakpoint switch (valkuil 18).
 */

function initScDrawer() {
  const drawer = document.getElementById('NavDrawer');
  if (!drawer || !drawer.classList.contains('sc-drawer')) return null;
  const viewport = drawer.querySelector('[data-sc-viewport]');
  if (!viewport) return null;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let activePanel = 'main';
  let settleTimer = null;

  const panels = () => Array.from(viewport.querySelectorAll('[data-sc-panel]'));
  const byName = (name) => panels().find((p) => p.dataset.scPanel === name);
  const sheetOf = (panel) => panel.querySelector('[data-sc-sheet]');

  function setInstant(on) {
    viewport.style.transition = on ? 'none' : '';
    panels().forEach((p) => {
      p.style.transition = on ? 'none' : '';
    });
  }

  function setOpenerExpanded(name) {
    drawer.querySelectorAll('[data-sc-open]').forEach((btn) => {
      btn.setAttribute('aria-expanded', btn.dataset.scOpen === name ? 'true' : 'false');
    });
  }

  function resetPanels() {
    if (settleTimer) {
      clearTimeout(settleTimer);
      settleTimer = null;
    }
    setInstant(true);
    panels().forEach((p) => {
      p.hidden = p.dataset.scPanel !== 'main';
      p.style.transform = '';
    });
    setOpenerExpanded('');
    const main = byName('main');
    // While the drawer is still display:none the sheet measures 0 — never
    // pin the viewport to that; auto keeps the in-flow main panel visible
    // and the first showPanel sets its own pixel base.
    const mainHeight = main ? sheetOf(main).offsetHeight : 0;
    viewport.style.height = mainHeight > 0 ? mainHeight + 'px' : '';
    viewport.getBoundingClientRect();
    requestAnimationFrame(() => setInstant(false));
    activePanel = 'main';
  }

  function positionDrawer() {
    const bar =
      document.querySelector('.sc-navbar-bar') || document.querySelector('.site-header');
    if (!bar) return;
    const rect = bar.getBoundingClientRect();
    drawer.style.setProperty(
      '--sc-drawer-top',
      Math.round(window.scrollY + rect.bottom + 8) + 'px'
    );
  }

  function showPanel(name, direction) {
    const next = byName(name);
    const current = byName(activePanel);
    if (!next || !current || next === current) return;

    if (settleTimer) {
      clearTimeout(settleTimer);
      settleTimer = null;
    }
    panels().forEach((p) => {
      if (p !== next && p !== current) {
        p.hidden = true;
        p.style.transform = '';
      }
    });

    next.hidden = false;
    // Measure the sheet inside the panel, never the panel: panels stretch
    // to the tallest sibling during the switch.
    const targetHeight = sheetOf(next).offsetHeight;
    const forward = direction > 0;
    const switchDuration = forward ? 400 : 250;

    const finish = () => {
      current.hidden = true;
      current.style.transform = '';
      current.classList.remove('sc-is-behind');
      next.style.transform = '';
      drawer.classList.remove('sc-swap-back');
    };

    if (reducedMotion.matches) {
      setInstant(true);
      viewport.style.height = targetHeight + 'px';
      finish();
      activePanel = name;
      setOpenerExpanded(name === 'main' ? '' : name);
      viewport.getBoundingClientRect();
      requestAnimationFrame(() => setInstant(false));
      return;
    }

    // Techweise-calibrated asymmetry: forward slides the new panel in from
    // the right while the old one parks at -30% under a dark veil (depth);
    // back is the exact reverse and faster (0.25s ease-in-out via the
    // sc-swap-back class).
    drawer.classList.toggle('sc-swap-back', !forward);
    setInstant(true);
    viewport.style.height = sheetOf(current).offsetHeight + 'px';
    if (forward) {
      next.style.transform = 'translateX(100%)';
    } else {
      next.style.transform = 'translateX(-30%)';
      next.classList.add('sc-is-behind');
    }
    viewport.getBoundingClientRect();
    setInstant(false);

    // Double rAF: one frame is not always enough for the style flush on
    // mobile, and without it the slide degrades to an instant jump.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        viewport.style.height = targetHeight + 'px';
        if (forward) {
          current.style.transform = 'translateX(-30%)';
          current.classList.add('sc-is-behind');
        } else {
          current.style.transform = 'translateX(100%)';
          next.classList.remove('sc-is-behind');
        }
        next.style.transform = 'translateX(0)';
      });
    });

    activePanel = name;
    setOpenerExpanded(forward ? name : '');
    settleTimer = setTimeout(finish, switchDuration + 100);
  }

  const onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    if (event.target.closest('.js-drawer-open-nav')) {
      // Capture phase: runs before theme.Drawers opens the drawer, so
      // only position here — the drawer is still display:none and any
      // height measurement would read 0. The reset runs on drawerOpen.
      positionDrawer();
      return;
    }

    const opener = event.target.closest('[data-sc-open]');
    if (opener && drawer.contains(opener)) {
      showPanel(opener.dataset.scOpen, 1);
      return;
    }

    if (event.target.closest('[data-sc-back]') && drawer.contains(event.target)) {
      showPanel('main', -1);
      return;
    }

    if (
      drawer.classList.contains('drawer--is-open') &&
      !drawer.contains(event.target) &&
      window.theme &&
      window.theme.NavDrawer
    ) {
      window.theme.NavDrawer.close();
    }
  };

  // Fired by theme.Drawers after the drawer is visible (display:block),
  // so the height measurement is real (valkuil 12: synchronous reset).
  const onDrawerOpen = () => {
    positionDrawer();
    resetPanels();
    // Fresh-open content fade (Techweise: 0.25s, delay 0.1, ease-in-out);
    // never on a panel switch. WAAPI with fill:backwards leaves no inline
    // styles behind, so nothing can stay invisible if this fails.
    if (!reducedMotion.matches) {
      const main = byName('main');
      const sheet = main && sheetOf(main);
      if (sheet && sheet.animate) {
        sheet.animate(
          { opacity: [0, 1], transform: ['translateY(1em)', 'translateY(0)'] },
          { duration: 250, delay: 100, easing: 'ease-in-out', fill: 'backwards' }
        );
      }
    }
  };

  // Rotation/viewport change while the sheet is open: reposition under
  // the bar and re-pin the viewport to the active sheet. No panel reset —
  // iOS fires resize on URL-bar collapse and that must not kick the
  // visitor back to the main panel.
  const onResize = () => {
    if (!drawer.classList.contains('drawer--is-open')) return;
    positionDrawer();
    const active = byName(activePanel);
    const sheet = active && sheetOf(active);
    if (sheet && sheet.offsetHeight > 0) {
      viewport.style.height = sheet.offsetHeight + 'px';
    }
  };

  const desktopMq = window.matchMedia('(min-width: 769px)');
  const onDesktop = (mq) => {
    if (
      mq.matches &&
      drawer.classList.contains('drawer--is-open') &&
      window.theme &&
      window.theme.NavDrawer
    ) {
      window.theme.NavDrawer.close();
    }
  };

  document.addEventListener('click', onClick, true);
  document.addEventListener('drawerOpen.NavDrawer', onDrawerOpen);
  window.addEventListener('resize', onResize);
  desktopMq.addEventListener('change', onDesktop);

  return () => {
    document.removeEventListener('click', onClick, true);
    document.removeEventListener('drawerOpen.NavDrawer', onDrawerOpen);
    window.removeEventListener('resize', onResize);
    desktopMq.removeEventListener('change', onDesktop);
  };
}

/*
 * Dark-glass sync (sc-header.md): the vendor swaps is-light away at a
 * fixed 250px scroll threshold, but the hero is much taller, so between
 * 250px and the hero's end the light milk-glass pane hovered over the
 * imagery and read as a washed-out beige. Keep is-light (dark glass +
 * light logo) for as long as the first content section is still behind
 * the bar; hand over to the light pane only once the bar clears it.
 * Pages that load without overlay mode (no header-wrapper--sticky) keep
 * the light pane throughout and never opt in. A MutationObserver corrects
 * the vendor's own toggles at the 250px boundary, whichever runs last;
 * toggle(force) that changes nothing does not re-fire the observer.
 */
function initScGlassSync() {
  const wrapper = document.getElementById('HeaderWrapper');
  const bar = document.getElementById('SiteHeader');
  if (!wrapper || !bar) return null;
  // Only with the pill restyle active: this module also loads when just
  // sc_cards is on, and correcting is-light on the vendor header would
  // break the "flag off = vendor behavior" guarantee.
  if (!bar.classList.contains('sc-navbar-bar')) return null;
  if (!wrapper.classList.contains('header-wrapper--sticky')) return null;
  const hero = document.querySelector('#MainContent .shopify-section');
  if (!hero) return null;

  let ticking = false;
  function sync() {
    ticking = false;
    const overHero =
      hero.getBoundingClientRect().bottom > bar.getBoundingClientRect().bottom;
    wrapper.classList.toggle('is-light', overHero);
  }
  function requestSync() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(sync);
  }

  const observer = new MutationObserver(requestSync);
  window.addEventListener('scroll', requestSync, { passive: true });
  window.addEventListener('resize', requestSync, { passive: true });
  observer.observe(wrapper, {
    attributes: true,
    attributeFilter: ['class'],
  });
  sync();

  return () => {
    window.removeEventListener('scroll', requestSync);
    window.removeEventListener('resize', requestSync);
    observer.disconnect();
  };
}

/*
 * Language/country selectors (sc-localization.liquid) are native
 * <details>: the browser handles open/close, this adds the dropdown
 * manners. One selector open at a time, close on outside click and on
 * Escape, and re-pin the height-locked mobile viewport (initScDrawer)
 * when a list expands in flow inside the sheet. Live DOM queries, so a
 * section reload needs no rebinding. The toggle event does not bubble;
 * a capturing document listener still sees it.
 */
function allScLocalization() {
  return Array.from(document.querySelectorAll('details[data-sc-localization]'));
}

document.addEventListener(
  'toggle',
  (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.hasAttribute('data-sc-localization')) {
      return;
    }
    if (details.open) {
      allScLocalization().forEach((other) => {
        if (other !== details && other.open) other.open = false;
      });
    }
    const sheet = details.closest('[data-sc-sheet]');
    const viewport = details.closest('[data-sc-viewport]');
    if (sheet && viewport && sheet.offsetHeight > 0) {
      viewport.style.height = sheet.offsetHeight + 'px';
    }
  },
  true
);

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  allScLocalization().forEach((details) => {
    if (details.open && !details.contains(event.target)) details.open = false;
  });
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  allScLocalization().forEach((details) => {
    if (!details.open) return;
    const hadFocus = details.contains(document.activeElement);
    details.open = false;
    const summary = details.querySelector('summary');
    if (hadFocus && summary) summary.focus();
  });
});

/*
 * Boot + editor lifecycle: both inits cache their elements, and a section
 * reload in the theme editor replaces those nodes. Tear down and rebind on
 * shopify:section:load (design mode only); the hover interception above
 * queries the live DOM and needs no rebinding.
 */
let scTeardowns = [];
function bootScHeader() {
  scTeardowns.forEach((teardown) => teardown && teardown());
  scTeardowns = [initScDrawer(), initScGlassSync()];
}

bootScHeader();

if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => bootScHeader());
}
